import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { getAllConfigs } from '../../services/configService';
import { getImageSrc } from '../../utils/imageHelper';
import { BUTTON_TEXT_SX, COLORS, FONTS } from '../../theme/tokens';
import { useTranslation } from 'react-i18next';

export const DEFAULT_HERO_IMAGE_URL = 'https://storage.googleapis.com/eladore-ruffles.appspot.com/images/full/c8152c5ccf58112719a5c06af88f2d30.jpg.webp';

const HeroSection = () => {
  const { t } = useTranslation();
  const [heroImageUrl, setHeroImageUrl] = useState(null);

  useEffect(() => {
    let ignore = false;

    const fetchHeroConfig = async () => {
      try {
        const configResponse = await getAllConfigs();

        if (!ignore) {
          const configuredImage = configResponse.success
            ? getImageSrc(configResponse.data?.homepage?.heroImage, 'full')
            : null;
          setHeroImageUrl(configuredImage || DEFAULT_HERO_IMAGE_URL);
        }
      } catch (error) {
        console.error('Error fetching hero image config:', error);
        if (!ignore) {
          setHeroImageUrl(DEFAULT_HERO_IMAGE_URL);
        }
      }
    };

    fetchHeroConfig();

    return () => {
      ignore = true;
    };
  }, []);

  const handleImageError = () => {
    setHeroImageUrl((currentUrl) => (
      currentUrl && currentUrl !== DEFAULT_HERO_IMAGE_URL
        ? DEFAULT_HERO_IMAGE_URL
        : null
    ));
  };

  return (
    <Box
      data-testid="hero-section"
      sx={{
        display: 'grid',
        width: '100%',
        overflow: 'hidden',
        gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
        height: { xs: 'auto', md: '520px' },
      }}
    >
      <Box
        sx={{
          order: { xs: 2, md: 1 },
          bgcolor: COLORS.surfaceWarm,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: { xs: '24px', md: '64px' },
          py: { xs: '32px', md: 0 },
          textAlign: { xs: 'center', md: 'left' },
        }}
      >
        <Typography
          component="p"
          sx={{
            fontFamily: FONTS.body,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '2.5px',
            textTransform: 'uppercase',
            color: COLORS.goldText,
            mb: 2,
          }}
        >
          {t('home.heroEyebrow')}
        </Typography>

        <Typography
          variant="h1"
          sx={{
            fontFamily: FONTS.heading,
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '3rem' },
            lineHeight: 1.12,
            color: COLORS.textPrimary,
            mb: 2,
          }}
        >
          {t('home.heroTitle')}
        </Typography>

        <Typography
          component="p"
          sx={{
            fontFamily: FONTS.body,
            fontSize: '1.0625rem',
            color: COLORS.textBody,
            maxWidth: 380,
            mb: 3,
            mx: { xs: 'auto', md: 0 },
          }}
        >
          {t('home.heroSubtitle')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
            justifyContent: { xs: 'center', md: 'flex-start' },
          }}
        >
          <Button
            variant="contained"
            component={Link}
            to="/featured"
            sx={{
              ...BUTTON_TEXT_SX,
              bgcolor: COLORS.green,
              color: '#FFFFFF',
              p: '15px 32px',
              fontFamily: FONTS.body,
              width: { xs: '100%', sm: 'auto' },
              height: { xs: '50px', sm: 'auto' },
              '&:hover': {
                bgcolor: COLORS.greenDark,
              },
            }}
          >
            {t('home.heroCta')}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          order: { xs: 1, md: 2 },
          position: 'relative',
          height: { xs: '400px', md: '100%' },
          overflow: 'hidden',
        }}
      >
        {heroImageUrl && (
          <img
            src={heroImageUrl}
            data-testid="hero-image"
            alt=""
            fetchpriority="high"
            aria-hidden="true"
            onError={handleImageError}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center center',
              display: 'block',
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default HeroSection;
